import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { ArrowBack, Download } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { StyledTypography } from '../styles/styles';
import axios from 'axios';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import ImpedanceGraph from '../../../components/CellInfo/ImpedanceGraph';
import CircuitParameters from '../../../components/CellInfo/CircuitParameters';
import BarcodeCard from '../../../components/Cards/BarcodeCard';
import CellImageCard from '../../../components/Cards/CellImageCard';
import HppcGraph from '../../../components/CellInfo/HppcGraph';
import ChargeDischargeGraph from '../../../components/CellInfo/ChargeDischargeGraph';
import CyclePlot from '../../../components/CellInfo/CyclePlot';
import { Loader } from '../../../components/Loaders/loader';
import { CircularProgress } from '@mui/material';
import { PDFDownloadLink, PDFViewer } from '@react-pdf/renderer';
import CellHealthCertificatePDF from './CellHealthCertificatePDF';
import CircuitParameterConstant from '../../../components/CellInfo/CircuitParameterConstant';

const staticCellId = '6697948e2f3802119cef0f21';
const CellHealthCertificate = () => {
  const navigate = useNavigate();
  const certificateRef = useRef(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [cellData, setCellData] = useState({
    battery_id: '',
    meta: {},
    electrical_properties: {},
    mechanical_properties: {},
    cell_images: [],
    cell_csvs: [],
  });
  const [ImpedanceCsvData, setImpedanceCsvData] = useState('');
  const [HppcCsvData, setHppcCsvData] = useState('');
  const [CycleCsvData, setCycleCsvData] = useState('');
  const [CellImage, setCellImage] = useState('');
  const [circuitParameters, setCircuitParameters] = useState({});

  useEffect(() => {
    const getCellData = async () => {
      try {
        const response = await axios.get(`${process.env.BACKEND_URL}batteries/id/${staticCellId}`);
        const data = response.data;
        setCellData(data);

        if (data.cell_csvs && data.cell_csvs.length > 0) {
          setImpedanceCsvData(data.cell_csvs[0]?.csv_path || '');
          setHppcCsvData(data.cell_csvs[1]?.csv_path || '');
          setCycleCsvData(data.cell_csvs[2]?.csv_path || '');
        }

        if (data.cell_images && data.cell_images.length > 0) {
          setCellImage(data.cell_images[0]?.image_path || '');
        }
      } catch (error) {
        console.error('Failed to fetch cell data:', error);
      }
    };
    getCellData();

    // Fetch circuit parameters for PDF
    const fetchCircuitParameters = async () => {
      try {
        const response = await axios.get(`${process.env.BACKEND_URL}batteries/circuit_parameters/${staticCellId}`);
        if (typeof response.data === 'object') {
          setCircuitParameters(response.data);
        }
      } catch (error) {
        setCircuitParameters({});
      }
    };
    fetchCircuitParameters();
  }, []);

  // Add print styles for PDF generation and scroll improvements
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @media print {
        * {
          -webkit-print-color-adjust: exact !important;
          color-adjust: exact !important;
        }
        .js-plotly-plot, .plotly {
          page-break-inside: avoid;
        }
        canvas {
          max-width: 100% !important;
          height: auto !important;
        }
      }
      
      /* Smooth scrolling for the certificate content */
      .certificate-scroll {
        scroll-behavior: smooth;
        -webkit-overflow-scrolling: touch;
      }
      
      /* Show scrollbar always for better UX */
      .certificate-scroll::-webkit-scrollbar {
        width: 8px !important;
      }
      
      .certificate-scroll::-webkit-scrollbar-track {
        background: rgba(255, 255, 255, 0.1) !important;
        border-radius: 4px !important;
      }
      
      .certificate-scroll::-webkit-scrollbar-thumb {
        background: #e8442d !important;
        border-radius: 4px !important;
      }
      
      .certificate-scroll::-webkit-scrollbar-thumb:hover {
        background: #ff6b35 !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const downloadFile = async (file) => {
    const response = await axios.get(`${process.env.BACKEND_URL}files/${file}`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', file);
    document.body.appendChild(link);
    link.click();
  };

  const generatePDF = async () => {
    if (!certificateRef.current) return;

    // Check if all data is loaded
    if (!cellData.battery_id || !CycleCsvData) {
      alert('Please wait for all data to load before generating PDF.');
      return;
    }

    setIsGeneratingPDF(true);

    try {
      // Wait for any async operations to complete and ensure charts are rendered
      await new Promise(resolve => setTimeout(resolve, 2000));

      const element = certificateRef.current;
      // Save original styles
      const originalBorderRadius = element.style.borderRadius;
      const originalPadding = element.style.padding;
      const originalBackground = element.style.background;
      const originalOverflow = element.style.overflow;
      const originalHeight = element.style.height;
      // Remove border radius and padding for PDF
      element.style.borderRadius = '0';
      element.style.padding = '0';
      element.style.background = '#000';
      element.style.overflow = 'visible';
      element.style.height = 'auto';

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#000000',
        width: element.scrollWidth,
        height: element.scrollHeight,
        scrollX: 0,
        scrollY: 0,
        logging: false,
        onclone: (clonedDoc) => {
          const clonedElement = clonedDoc.querySelector('[data-certificate-content]');
          if (clonedElement) {
            clonedElement.style.borderRadius = '0';
            clonedElement.style.padding = '0';
            clonedElement.style.background = '#000';
            clonedElement.style.overflow = 'visible';
            clonedElement.style.height = 'auto';
            clonedElement.style.boxSizing = 'border-box';
          }
        }
      });

      // Restore original styles
      element.style.borderRadius = originalBorderRadius;
      element.style.padding = originalPadding;
      element.style.background = originalBackground;
      element.style.overflow = originalOverflow;
      element.style.height = originalHeight;

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidthPx = canvas.width;
      const imgHeightPx = canvas.height;
      // Scale so the image width matches the PDF width
      const imgWidth = pdfWidth;
      const scale = imgWidth / imgWidthPx;
      const imgHeight = imgHeightPx * scale;

      let position = 0;
      let pageHeightLeft = imgHeight;
      let pageNum = 0;
      const pageHeight = pdfHeight;

      while (pageHeightLeft > 0) {
        if (pageNum > 0) pdf.addPage();
        // Calculate the portion of the image to draw
        const sourceY = (imgHeightPx * position) / imgHeight;
        const sourceHeight = (imgHeightPx * Math.min(pageHeight, pageHeightLeft)) / imgHeight;
        // Create a temporary canvas for this page
        const tempCanvas = document.createElement('canvas');
        const tempCtx = tempCanvas.getContext('2d');
        tempCanvas.width = imgWidthPx;
        tempCanvas.height = sourceHeight;
        tempCtx.drawImage(
          canvas,
          0, sourceY, imgWidthPx, sourceHeight,
          0, 0, imgWidthPx, sourceHeight
        );
        const pageImgData = tempCanvas.toDataURL('image/png');
        pdf.addImage(
          pageImgData,
          'PNG',
          0,
          0,
          imgWidth,
          (sourceHeight * scale)
        );
        position += pageHeight;
        pageHeightLeft -= pageHeight;
        pageNum++;
      }

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `CellHealthCertificate_${cellData.battery_id || 'unknown'}_${timestamp}.pdf`;

      pdf.save(filename);

      // Show success message
      alert('Certificate PDF generated successfully!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again. Make sure all charts are loaded.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  if (!cellData.meta && !cellData.electrical_properties) {
    return <Loader />;
  }

  return (
    <Box sx={{
      background: 'linear-gradient(135deg, #000 0%, #1a1a1a 50%, #000 100%)',
      borderRadius: '2rem',
      p: 3,
      fontFamily: 'Bai Jamjuree',
      color: '#fff',
      height: '90vh',
      position: 'relative',
      border: '1px solid rgba(232, 68, 45, 0.2)',
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      boxSizing: 'border-box',
      overflow: 'hidden',
    }}>

      {/* Main Content */}
      <Box sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        overflowY: 'auto',
        maxHeight: '80vh',
        position: 'relative',
        height: 'calc(90vh - 60px)', // Account for parent padding
        mb: 0.5 // Space for buttons
      }}>
        <Box
          ref={certificateRef}
          data-certificate-content
          className="certificate-scroll"
          sx={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            background: 'linear-gradient(135deg, #000 0%, #1a1a1a 50%, #000 100%)',
            borderRadius: '2rem',
            p: 2,
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#e8442d',
              borderRadius: '4px',
              '&:hover': {
                background: '#ff6b35',
              },
            },
          }}
        >
          {/* Cycle Plot - Hidden but loaded */}
          {(staticCellId && CycleCsvData) ? (
            <Box sx={{ 
              mb: 4, 
              position: 'absolute',
              left: '-9999px',
              visibility: 'hidden',
              pointerEvents: 'none'
            }}>
              <CyclePlot
                cellId={staticCellId}
                CellCycleData={CycleCsvData}
                downloadFile={downloadFile}
              />
            </Box>
          ) : (
            <Box>
              <Loader />
            </Box>
          )}


          {/* Circuit Parameters - Hidden but loaded */}
          {staticCellId ? (
            <Box sx={{ 
              mb: 4, 
              position: 'absolute',
              left: '-9999px',
              visibility: 'hidden',
              pointerEvents: 'none'
            }}>
              <CircuitParameters cellId={staticCellId} showEnhanced={true} />
            </Box>
          ) : (
            <Box>
              <Loader />
            </Box>
          )}
          <Box sx={{ mt: 0, mb: 0 }}>
            <StyledTypography variant="h6" sx={{ color: '#e8442d', fontWeight: 700, mb: 2 }}>
              Your Cell Health Certificate
            </StyledTypography>
            <Box sx={{ border: '2px solid #e8442d', borderRadius: '12px', overflow: 'auto', minHeight: 600, maxHeight: '80vh' }}>
              <PDFViewer width="100%" height={600} style={{ minHeight: 600, height: '80vh' }}>
                <CellHealthCertificatePDF />
                {/* <CellHealthCertificatePDF
                  cellData={cellData}
                  circuitParameters={circuitParameters}
                  soh={cellData.meta?.soh || ''}
                  risk={cellData.meta?.risk_score || ''}
                  rul={cellData.meta?.rul || ''}
                  cellImage={CellImage ? `${process.env.BACKEND_URL}files/${CellImage}` : ''}
                  barcode={cellData.battery_id ? `${process.env.BACKEND_URL}files/barcode/${cellData.battery_id}` : ''}
                  date={new Date().toLocaleDateString()}
                  recipient={cellData.meta?.owner || 'Cell Owner'}
                /> */}
              </PDFViewer>
            </Box>
          </Box>

        </Box>
      </Box>
    </Box>
  );
};

export default CellHealthCertificate;
